import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';
import FunwooAPI, { GetPublishedPostResponseEntity, PublishedPostDetailEntity } from '../../swagger';
import FullPage from '@/src/components/UI/FullPage';
import OpenGraph from '@/components/OpenGraph';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ArticleBody } from '@/constants/article';
import ArticleReader from '@/src/components/UI/ArticleReader';

interface Params extends NodeJS.Dict<string> {
  id: string;
}

interface Props extends Omit<PublishedPostDetailEntity, 'body'> {
  body: Array<ArticleBody>;
  recommendPosts: GetPublishedPostResponseEntity;
}

const Post: React.FC<Props> = ({ title, coverImage, uuid, body, recommendPosts, relatedListings }) => {
  return (
    <FullPage hasPadding hasHeaderPlaceHolder>
      <OpenGraph
        title={`FUNWOO 德載國際不動產 - ${title}`}
        type={'blog'}
        imageUrl={coverImage}
        url={`https://funwoo.com.tw/media/${uuid}`}
      />
      <ArticleReader
        body={body}
        recommendPosts={recommendPosts}
        linkPrefix={'media'}
        relatedListings={relatedListings}
      />
    </FullPage>
  );
};

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const { data } = await FunwooAPI.blog.getPublishedPosts({
    category: 'MEDIA',
  });
  const paths = data.items.map((post) => ({
    params: { id: encodeURI(post.uuid) },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props, Params> = async ({ params, locale }) => {
  if (!params?.id || !locale) {
    return {
      notFound: true,
    };
  } else {
    const {
      data: { body, ...data },
    } = await FunwooAPI.blog.getPublishedPostByUuid(decodeURI(params?.id));
    const { data: recommendPosts } = await FunwooAPI.blog.getRecommendedPosts(decodeURIComponent(data.id), {
      count: 3,
    });

    const parsedBody = body ? JSON.parse(body) : [];

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        ...data,
        body: parsedBody,
        recommendPosts,
      },
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  }
};

export default Post;
